import { NotificationsNoneOutlined } from "@mui/icons-material";
import {
  Box,
  Skeleton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import type { Task } from "fhir/r5";

import { NotificationItemCard } from "./NotificationItemCard";

export interface NotificationsPanelBodyProps {
  unreadCount: number;
  loading: boolean;
  accessType?: string | null;
  tasks: Task[];
  updatingById: Record<string, boolean>;
  closeHint?: string;
  showUnreadSummary?: boolean;
  onToggleReadStatus: (task: Task) => void;
  onAccessMedicalRecord: (task: Task) => void;
  onRespondQuestionnaire: (task: Task) => void;
}

function NotificationSkeleton() {
  return (
    <Box
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        p: 1.25,
        display: "flex",
        gap: 1,
      }}
    >
      <Skeleton variant="rounded" width={34} height={34} sx={{ flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Skeleton variant="text" width="68%" height={18} />
        <Skeleton variant="text" width="90%" sx={{ mt: 0.5 }} />
        <Skeleton variant="text" width="42%" sx={{ mt: 0.75 }} />
      </Box>
    </Box>
  );
}

export function NotificationsPanelBody({
  unreadCount,
  loading,
  accessType,
  tasks,
  updatingById,
  closeHint,
  showUnreadSummary = true,
  onToggleReadStatus,
  onAccessMedicalRecord,
  onRespondQuestionnaire,
}: NotificationsPanelBodyProps) {
  const theme = useTheme();

  return (
    <Stack spacing={1}>
      {showUnreadSummary || closeHint ? (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          {showUnreadSummary ? (
            unreadCount > 0 ? (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1,
                  py: 0.35,
                  borderRadius: 99,
                  bgcolor: alpha(theme.palette.warning.main, 0.12),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.22)}`,
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: "warning.main",
                  }}
                />
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="warning.dark"
                  sx={{ fontSize: "0.6875rem" }}
                >
                  {unreadCount === 1 ? "1 não lida" : `${unreadCount} não lidas`}
                </Typography>
              </Box>
            ) : (
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                sx={{ fontSize: "0.75rem" }}
              >
                Tudo em dia
              </Typography>
            )
          ) : (
            <Box />
          )}
          {closeHint ? (
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.6875rem" }}>
              {closeHint}
            </Typography>
          ) : null}
        </Stack>
      ) : null}

      {loading ? (
        <Stack spacing={1.5}>
          <NotificationSkeleton />
          <NotificationSkeleton />
        </Stack>
      ) : tasks.length === 0 ? (
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={1}
          sx={{ py: 3.5, px: 1, textAlign: "center" }}
        >
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(theme.palette.primary.main, 0.08),
            }}
          >
            <NotificationsNoneOutlined
              sx={{ fontSize: 26, color: "primary.main", opacity: 0.7 }}
            />
          </Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: "0.875rem" }}>
            Nenhuma notificação
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            maxWidth={260}
            sx={{ fontSize: "0.75rem", lineHeight: 1.45 }}
          >
            Novidades sobre questionários, prontuário e tarefas aparecerão aqui.
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={1.5} sx={{ pb: 0.25 }}>
          {tasks.map((task, index) => (
            <NotificationItemCard
              key={task.id ?? `task-${index}`}
              task={task}
              accessType={accessType}
              isUpdating={Boolean(updatingById[task.id ?? ""])}
              onToggleReadStatus={onToggleReadStatus}
              onAccessMedicalRecord={onAccessMedicalRecord}
              onRespondQuestionnaire={onRespondQuestionnaire}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}
